import {
  cancelPendingWhatsAppResponseSchema,
  completeWhatsAppEmbeddedSignupRequestSchema,
  completeWhatsAppEmbeddedSignupResponseSchema,
  createWhatsAppPhoneRequestSchema,
  createWhatsAppPhoneResponseSchema,
  createWhatsAppTemplateRequestSchema,
  disconnectWhatsAppResponseSchema,
  editWhatsAppTemplateRequestSchema,
  registerWhatsAppPhoneRequestSchema,
  registerWhatsAppPhoneResponseSchema,
  attachWhatsAppPhoneResponseSchema,
  requestWhatsAppPhoneVerificationCodeRequestSchema,
  requestWhatsAppPhoneVerificationCodeResponseSchema,
  selectWhatsAppPhoneNumbersRequestSchema,
  selectWhatsAppPhoneNumbersResponseSchema,
  switchWhatsAppWorkspacePhoneRequestSchema,
  switchWhatsAppWorkspacePhoneResponseSchema,
  verifyWhatsAppPhoneCodeRequestSchema,
  verifyWhatsAppPhoneCodeResponseSchema,
  whatsAppConnectionResponseSchema,
  whatsAppEmbeddedSignupConfigResponseSchema,
  whatsAppPhoneRegistrationStatusResponseSchema,
  whatsAppTemplateMutationResponseSchema,
  whatsAppTemplatesListResponseSchema,
  whatsAppTemplatesSyncResponseSchema,
  whatsAppTemplateHeaderImageDownloadResponseSchema,
  updateWhatsAppBusinessProfilePictureResponseSchema,
  whatsAppWorkspacePhoneOptionsResponseSchema,
  type CancelPendingWhatsAppResponse,
  type CompleteWhatsAppEmbeddedSignupRequest,
  type CompleteWhatsAppEmbeddedSignupResponse,
  type CreateWhatsAppPhoneRequest,
  type CreateWhatsAppPhoneResponse,
  type CreateWhatsAppTemplateRequest,
  type DisconnectWhatsAppResponse,
  type EditWhatsAppTemplateRequest,
  type RegisterWhatsAppPhoneRequest,
  type RegisterWhatsAppPhoneResponse,
  type AttachWhatsAppPhoneResponse,
  type RequestWhatsAppPhoneVerificationCodeRequest,
  type RequestWhatsAppPhoneVerificationCodeResponse,
  type SelectWhatsAppPhoneNumbersResponse,
  type SwitchWhatsAppWorkspacePhoneResponse,
  type VerifyWhatsAppPhoneCodeRequest,
  type VerifyWhatsAppPhoneCodeResponse,
  type WhatsAppConnectionResponse,
  type WhatsAppEmbeddedSignupConfigResponse,
  type WhatsAppPhoneRegistrationStatusResponse,
  type WhatsAppTemplateMutationResponse,
  type WhatsAppTemplatesListResponse,
  type WhatsAppTemplatesSyncResponse,
  type WhatsAppTemplateHeaderImageDownloadResponse,
  type UpdateWhatsAppBusinessProfilePictureResponse,
  type WhatsAppWorkspacePhoneOptionsResponse,
} from "@multi-chat/shared";
import { apiDelete, apiGet, apiPost, apiPostFormData } from "./client.js";

function orgBase(organizationId: number): string {
  return `/organizations/${organizationId}/whatsapp`;
}

export async function getEmbeddedSignupConfig(
  organizationId: number,
  signal?: AbortSignal,
): Promise<WhatsAppEmbeddedSignupConfigResponse> {
  return whatsAppEmbeddedSignupConfigResponseSchema.parse(
    await apiGet(`${orgBase(organizationId)}/embedded-signup/config`, signal),
  );
}

export async function completeEmbeddedSignup(
  organizationId: number,
  payload: CompleteWhatsAppEmbeddedSignupRequest,
  signal?: AbortSignal,
): Promise<CompleteWhatsAppEmbeddedSignupResponse> {
  const body = completeWhatsAppEmbeddedSignupRequestSchema.parse(payload);
  return completeWhatsAppEmbeddedSignupResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/embedded-signup/complete`, body, signal),
  );
}

export async function selectPhoneNumbers(
  organizationId: number,
  externalPhoneNumberIds: string[],
  signal?: AbortSignal,
): Promise<SelectWhatsAppPhoneNumbersResponse> {
  const body = selectWhatsAppPhoneNumbersRequestSchema.parse({ externalPhoneNumberIds });
  return selectWhatsAppPhoneNumbersResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/phone-numbers/select`, body, signal),
  );
}

export async function switchWorkspacePhone(
  organizationId: number,
  externalPhoneNumberId: string,
  signal?: AbortSignal,
): Promise<SwitchWhatsAppWorkspacePhoneResponse> {
  const body = switchWhatsAppWorkspacePhoneRequestSchema.parse({ externalPhoneNumberId });
  return switchWhatsAppWorkspacePhoneResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/workspace-phone`, body, signal),
  );
}

export async function getWorkspacePhoneOptions(
  organizationId: number,
  signal?: AbortSignal,
): Promise<WhatsAppWorkspacePhoneOptionsResponse> {
  return whatsAppWorkspacePhoneOptionsResponseSchema.parse(
    await apiGet(`${orgBase(organizationId)}/workspace-phone-options`, signal),
  );
}

export async function createWhatsAppPhone(
  organizationId: number,
  payload: CreateWhatsAppPhoneRequest,
  signal?: AbortSignal,
): Promise<CreateWhatsAppPhoneResponse> {
  const body = createWhatsAppPhoneRequestSchema.parse(payload);
  return createWhatsAppPhoneResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/phones`, body, signal),
  );
}

export async function getWhatsAppPhoneRegistrationStatus(
  organizationId: number,
  phoneNumberId: string,
  signal?: AbortSignal,
): Promise<WhatsAppPhoneRegistrationStatusResponse> {
  return whatsAppPhoneRegistrationStatusResponseSchema.parse(
    await apiGet(
      `${orgBase(organizationId)}/phones/${encodeURIComponent(phoneNumberId)}/registration-status`,
      signal,
    ),
  );
}

export async function requestWhatsAppPhoneVerificationCode(
  organizationId: number,
  phoneNumberId: string,
  payload: RequestWhatsAppPhoneVerificationCodeRequest,
  signal?: AbortSignal,
): Promise<RequestWhatsAppPhoneVerificationCodeResponse> {
  const body = requestWhatsAppPhoneVerificationCodeRequestSchema.parse(payload);
  return requestWhatsAppPhoneVerificationCodeResponseSchema.parse(
    await apiPost(
      `${orgBase(organizationId)}/phones/${encodeURIComponent(phoneNumberId)}/request-code`,
      body,
      signal,
    ),
  );
}

export async function verifyWhatsAppPhoneCode(
  organizationId: number,
  phoneNumberId: string,
  payload: VerifyWhatsAppPhoneCodeRequest,
  signal?: AbortSignal,
): Promise<VerifyWhatsAppPhoneCodeResponse> {
  const body = verifyWhatsAppPhoneCodeRequestSchema.parse(payload);
  return verifyWhatsAppPhoneCodeResponseSchema.parse(
    await apiPost(
      `${orgBase(organizationId)}/phones/${encodeURIComponent(phoneNumberId)}/verify-code`,
      body,
      signal,
    ),
  );
}

export async function registerWhatsAppPhone(
  organizationId: number,
  phoneNumberId: string,
  payload: RegisterWhatsAppPhoneRequest,
  signal?: AbortSignal,
): Promise<RegisterWhatsAppPhoneResponse> {
  const body = registerWhatsAppPhoneRequestSchema.parse(payload);
  return registerWhatsAppPhoneResponseSchema.parse(
    await apiPost(
      `${orgBase(organizationId)}/phones/${encodeURIComponent(phoneNumberId)}/register`,
      body,
      signal,
    ),
  );
}

export async function attachWhatsAppPhone(
  organizationId: number,
  phoneNumberId: string,
  signal?: AbortSignal,
): Promise<AttachWhatsAppPhoneResponse> {
  return attachWhatsAppPhoneResponseSchema.parse(
    await apiPost(
      `${orgBase(organizationId)}/phones/${encodeURIComponent(phoneNumberId)}/attach`,
      {},
      signal,
    ),
  );
}

export async function getConnection(
  organizationId: number,
  signal?: AbortSignal,
): Promise<WhatsAppConnectionResponse> {
  return whatsAppConnectionResponseSchema.parse(
    await apiGet(orgBase(organizationId), signal),
  );
}

export async function cancelPending(
  organizationId: number,
  signal?: AbortSignal,
): Promise<CancelPendingWhatsAppResponse> {
  return cancelPendingWhatsAppResponseSchema.parse(
    await apiDelete(`${orgBase(organizationId)}/pending`, signal),
  );
}

export async function disconnect(
  organizationId: number,
  signal?: AbortSignal,
): Promise<DisconnectWhatsAppResponse> {
  return disconnectWhatsAppResponseSchema.parse(
    await apiDelete(orgBase(organizationId), signal),
  );
}

export async function listWhatsAppTemplates(
  organizationId: number,
  signal?: AbortSignal,
): Promise<WhatsAppTemplatesListResponse> {
  return whatsAppTemplatesListResponseSchema.parse(
    await apiGet(`${orgBase(organizationId)}/templates`, signal),
  );
}

export async function syncWhatsAppTemplates(
  organizationId: number,
  signal?: AbortSignal,
): Promise<WhatsAppTemplatesSyncResponse> {
  return whatsAppTemplatesSyncResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/templates/sync`, undefined, signal),
  );
}

export async function createWhatsAppTemplate(
  organizationId: number,
  payload: CreateWhatsAppTemplateRequest,
  options?: { headerImage?: File | null; signal?: AbortSignal },
): Promise<WhatsAppTemplateMutationResponse> {
  const body = createWhatsAppTemplateRequestSchema.parse(payload);
  const signal = options?.signal;
  if (options?.headerImage) {
    const form = new FormData();
    form.set("payload", JSON.stringify(body));
    form.set("headerImage", options.headerImage, options.headerImage.name);
    return whatsAppTemplateMutationResponseSchema.parse(
      await apiPostFormData(`${orgBase(organizationId)}/templates`, form, signal),
    );
  }
  return whatsAppTemplateMutationResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/templates`, body, signal),
  );
}

export async function attachWhatsAppTemplateHeaderImage(
  organizationId: number,
  templateId: number,
  file: File,
  signal?: AbortSignal,
): Promise<WhatsAppTemplateMutationResponse> {
  const form = new FormData();
  form.set("file", file, file.name);
  return whatsAppTemplateMutationResponseSchema.parse(
    await apiPostFormData(
      `${orgBase(organizationId)}/templates/${templateId}/header-image`,
      form,
      signal,
    ),
  );
}

export async function createWhatsAppTemplateHeaderImageDownloadUrl(
  organizationId: number,
  templateId: number,
  signal?: AbortSignal,
): Promise<WhatsAppTemplateHeaderImageDownloadResponse> {
  return whatsAppTemplateHeaderImageDownloadResponseSchema.parse(
    await apiPost(
      `${orgBase(organizationId)}/templates/${templateId}/header-image/download-url`,
      undefined,
      signal,
    ),
  );
}

export async function updateWhatsAppBusinessProfilePicture(
  organizationId: number,
  phoneNumberId: string,
  file: File,
  signal?: AbortSignal,
): Promise<UpdateWhatsAppBusinessProfilePictureResponse> {
  const form = new FormData();
  form.set("file", file, file.name);
  return updateWhatsAppBusinessProfilePictureResponseSchema.parse(
    await apiPostFormData(
      `${orgBase(organizationId)}/phones/${encodeURIComponent(phoneNumberId)}/profile-picture`,
      form,
      signal,
    ),
  );
}

export async function updateWhatsAppTemplate(
  organizationId: number,
  templateId: number,
  payload: EditWhatsAppTemplateRequest,
  signal?: AbortSignal,
): Promise<WhatsAppTemplateMutationResponse> {
  const body = editWhatsAppTemplateRequestSchema.parse(payload);
  return whatsAppTemplateMutationResponseSchema.parse(
    await apiPost(`${orgBase(organizationId)}/templates/${templateId}`, body, signal),
  );
}

export async function deleteWhatsAppTemplate(
  organizationId: number,
  templateId: number,
  signal?: AbortSignal,
): Promise<WhatsAppTemplateMutationResponse> {
  return whatsAppTemplateMutationResponseSchema.parse(
    await apiDelete(`${orgBase(organizationId)}/templates/${templateId}`, signal),
  );
}
